import { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import HeroWorldClass from '../components/HeroWorldClass';
import TemplatesSection from '../components/TemplatesSection';
import CinematicShowcase from '../components/CinematicShowcase';
import TrustedBy from '../components/TrustedBy';
import HowItWorks from '../components/HowItWorks';
import WhyChooseUs from '../components/WhyChooseUs';
import Comparison from '../components/Comparison';
import ROICalculator from '../components/ROICalculator';
import Pricing from '../components/Pricing';
import FAQSection from '../components/FAQSection';
import Footer from '../components/SiteFooter';
import AuthDialog from '../components/AuthDialog';
import AssistantWidget from '../components/AssistantWidget';
import TrendingSouls from '../components/TrendingSouls';
import SoulSpotlight from '../components/SoulSpotlight';
import { Toaster } from '../components/ui/toaster';
import { toast } from '../hooks/use-toast';
import { useScrollReveal } from '../hooks/useScrollReveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_TO_PACKAGE = {
  Standard: 'standard',
  Pro: 'pro',
  Scale: 'scale',
};

export default function LandingPage() {
  const [authOpen, setAuthOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  useScrollReveal();

  // Detect return from Stripe (either success or cancel)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (!sid) return;
    (async () => {
      toast({ title: 'Checking your payment...', description: 'One moment.' });
      let attempts = 0;
      while (attempts < 6) {
        const r = await fetch(`${API}/payments/checkout/status/${sid}`);
        if (r.ok) {
          const d = await r.json();
          if (d.payment_status === 'paid') {
            toast({ title: 'Payment successful!', description: `You are now on the ${d.metadata?.plan_name || ''} plan.` });
            window.history.replaceState({}, document.title, window.location.pathname + '#pricing');
            return;
          }
          if (d.status === 'expired') {
            toast({ title: 'Session expired', description: 'Please try the checkout again.' });
            return;
          }
        }
        await new Promise((res) => setTimeout(res, 2000));
        attempts++;
      }
      toast({ title: 'Still processing', description: 'Your payment is being processed. Check email shortly.' });
    })();
  }, []);

  const handleChoosePlan = async (planName) => {
    if (planName === 'Free') {
      setAuthOpen(true);
      return;
    }
    if (planName === 'Enterprise' || planName === 'merchant') {
      toast({ title: 'Sales will reach out shortly', description: 'This is a demo message.' });
      return;
    }
    const packageId = PLAN_TO_PACKAGE[planName];
    if (!packageId) {
      setAuthOpen(true);
      return;
    }
    setCheckoutLoading(true);
    try {
      const r = await fetch(`${API}/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin,
        }),
      });
      if (!r.ok) throw new Error(`Checkout failed (${r.status})`);
      const data = await r.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (e) {
      toast({ title: 'Checkout failed', description: e.message || 'Please try again in a moment.' });
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="relative">
      <Navbar onGetStarted={() => setAuthOpen(true)} />
      <HeroWorldClass onSignIn={() => setAuthOpen(true)} />
      <TemplatesSection />
      <CinematicShowcase />
      <TrustedBy />
      <HowItWorks />
      <WhyChooseUs />
      <SoulSpotlight />
      <TrendingSouls />
      <Comparison />
      <ROICalculator onCta={() => setAuthOpen(true)} />
      <Pricing onChoose={handleChoosePlan} loading={checkoutLoading} />
      <FAQSection />
      <Footer />
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      <AssistantWidget />
      <Toaster />
    </div>
  );
}
